export const FEATURE_IDS = [
  'events',
  'ai-planner',
  'checkins',
  'offline-maps',
  'city-walk',
  'treasure-hunt',
  'premium',
];

export const ROADMAP_IDS = ['q2-2026', 'testflight', 'app-store', 'expansion'];

export const translations = {
  en: {
    meta: {
      home: 'omw — Real-world exploration & play',
      about: 'About — omw',
      support: 'Support — omw',
      privacy: 'Privacy Policy — omw',
      terms: 'Terms of Service — omw',
    },
    lang: { label: 'Language', en: 'EN', zh: '中文' },
    nav: {
      features: 'Features',
      premium: 'Premium',
      roadmap: 'Roadmap',
      about: 'About',
      support: 'Support',
      testflight: 'TestFlight',
      joinTestflight: 'Join TestFlight',
    },
    hero: {
      brand: 'omw',
      headline: 'Find the unfound.',
      typed:
        'Only offline community is REAL community.',
      ctaPrimary: 'Join TestFlight waitlist',
      ctaSecondary: 'See features',
      mapTitle: 'omw city map',
    },
    coupon: {
      normal: 'Normal',
      medium: 'Medium',
      epic: 'Epic',
    },
    features: {
      events: {
        eyebrow: 'Real-world Social',
        title: 'Events & Gatherings',
        body: 'Organise a football kick-about, a language exchange, or a pop-up treasure race. Discover what is actually happening near you and meet people through shared activities — not endless swiping.',
        bullets: [
          'Map-based event discovery',
          'Make new friends in context — connect through shared activity, not random profiles',
          'Today & live story rail — see what\'s happening now nearby, so you never miss a spontaneous gathering',
          'AI along-route picks: cafés, restaurants, and supply stops matched to your event route',
        ],
        mediaLabel: 'Events · gathering & discovery',
      },
      'ai-planner': {
        eyebrow: 'Event & Location-Driven',
        title: 'AI Trip Planner',
        body: 'An event- and location-driven planning agent inside the app. It reads the events, location shares, and city walk scripts you care about — then builds an optimized day so you can comfortably finish everything on your list and explore more of your city or region.',
        bullets: [
          'Pulls from saved events, shared spots, and urban exploration routes',
          'Auto-optimized routes, timing, and transit between stops',
          'One plan shaped around your interests — not a generic template',
        ],
        mediaLabel: 'AI planner · event-driven itinerary',
      },
      checkins: {
        eyebrow: 'Places That Matter',
        title: 'Location Check-ins',
        body: 'Share a unique spot with the community — fishing spots, hidden trails, climbing crags, or street art. AI estimates calorie burn, builds a gear checklist, and generates safety tips from the route, terrain, and weather.',
        bullets: [
          'AI calorie estimates from distance, elevation, and activity type',
          'Auto-generated gear and packing checklist for each location',
          'Location-aware safety tips powered by terrain, altitude, and live weather',
        ],
        mediaLabel: 'Check-in · sticker & event flow',
      },
      'offline-maps': {
        eyebrow: 'Go Further',
        title: 'Outdoor Exploration',
        body: 'More than offline maps. Record and share beatiful but unfound trails.',
        bullets: [
          'Offline map packs for hiking areas',
          'GPS track recorder with elevation',
          'Share routes in the community',
        ],
        mediaLabel: 'Offline maps · track recorder',
      },
      'city-walk': {
        eyebrow: 'City Stories',
        title: 'New version of City Walk',
        body: 'Have you ever watched a movie called "The Da Vinci Code"? Let make the city walk be like that.',
        bullets: [
          'Stage-based scripts tied to real locations',
          'AI-powered script generation for details you need based on your route/idea/theme/location/etc.',
          'Collaborate with others in the community; go explore with your friends.',
          'AI along-route shop picks — cafés, food, and rest stops woven into your urban exploration',
        ],
        mediaLabel: 'City walk script · stage map',
      },
      'treasure-hunt': {
        eyebrow: 'Discover & Hide',
        title: 'Treasure Hunt',
        bullets: [
          'Turn your city into a playground. Hide a capsule in a park, down an alley, or outside a café — attach a riddle, photo, or small gift. Others hunt on foot; find the surprise!',
          'Find and collect blind-box treasures that merchants hide around the city.',
        ],
        mediaLabel: 'Treasure hunt · map & capsule flow',
      },
      premium: {
        title: 'Safety & Sealed Letters',
        body: 'For moments that matter. Premium unlocks one-tap SOS with live GPS, community SOS broadcast to nearby helpers on the map, zero-knowledge encrypted sealed letters, and instant alerts to your chosen emergency contacts.',
        bullets: [
          'One-tap SOS — emergency contacts notified instantly with your live location',
          'SOS broadcast: your alert goes live on the map and prompts nearby community members who can help',
          'Offline backup via encrypted BLE mesh relay when there is no signal',
          'End-to-end encrypted sealed letters with a 24-hour safety grace period',
        ],
        mediaLabel: 'Premium · SOS & sealed letter',
        linkLabel: 'Join TestFlight waitlist',
      },
    },
    roadmap: {
      title: 'Roadmap',
      lead: 'May 2026 today — TestFlight in Jul–Sep, App Store mid-September, regional expansion by year-end.',
      phases: {
        'q2-2026': {
          period: 'Q2 2026 · Now',
          phase: 'Build & Polish',
          status: 'In Progress',
          tasks: [
            'Core app: hunt, scripts, events, offline maps',
            'App Store privacy & compliance review',
            'Marketing site & TestFlight prep',
          ],
          details: [
            'Feature-complete iOS build under active QA.',
            'Privacy policy and App Privacy Details aligned with App Store Connect.',
          ],
        },
        testflight: {
          period: 'Jul – Sep 2026',
          phase: 'TestFlight Beta',
          status: 'Upcoming',
          tasks: [
            'Public TestFlight invite wave',
            '1-year omw Premium for all TestFlight registrants',
          ],
          details: [
            'Everyone who registers during the TestFlight beta receives one year of omw Premium at no cost.',
          ],
        },
        'app-store': {
          period: 'Mid-Sep 2026',
          phase: 'App Store Launch',
          status: 'Planned',
          tasks: [
            'iOS App Store release',
            'Australia launch market',
            'Launch city walk script bundle',
          ],
          details: [
            'Target: mid-September 2026 on the App Store.',
            'Initial market: Australia (Canberra, Sydney, Melbourne).',
          ],
        },
        expansion: {
          period: 'Q4 2026',
          phase: 'Regional Expansion',
          status: 'Planned',
          tasks: [
            'Expand to NZ, Singapore, and selected cities',
            'Localised script marketplace',
            'Community ambassador programme',
          ],
          details: [
            'Roll out from Australia to other regions before year-end.',
            'Partner with local creators for city-specific script content.',
          ],
        },
      },
    },
    download: {
      subtitle: 'TestFlight beta · Jul–Sep 2026 · Lifetime membership for testers',
      iosLabel: 'Join iOS TestFlight beta',
      androidLabel: 'Android coming soon',
    },
    progress: {
      label: 'Launch progress',
      pct: '{pct}% to TestFlight',
    },
    homeIntro: {
      eyebrow: 'What is omw',
      title: 'Your city, turned into a place to explore and meet people',
      body: 'omw ("on my way") is a real-world exploration and social app. Discover events happening near you, follow location-based story walks, uncover hidden outdoor routes, and hunt for community treasures — then meet people through shared activities instead of endless scrolling. Built-in Premium safety tools keep every adventure covered.',
      bullets: [
        'Map-based events & gatherings near you',
        'AI trip planner, offline maps & GPS track recording',
        'City Walk story scripts & community treasure hunts',
        'Premium safety: one-tap SOS & encrypted sealed letters',
      ],
      cta: 'Learn more about us',
    },
    about: {
      hero: {
        eyebrow: 'About omw',
        title: 'We help people rediscover the real world — together',
        subtitle: 'omw ("on my way") turns your city into somewhere to explore, play, and meet people offline.',
      },
      business: {
        title: 'What we do',
        body: [
          'omw is a real-world exploration and social platform. We bring together map-based event discovery, an AI trip planner, location check-ins, offline maps, location-based "City Walk" story scripts, and a community treasure hunt — so people can find what is genuinely happening around them and connect through shared activities.',
          'The problem we solve: modern social apps keep people scrolling alone, while everything worth doing in the real world — events, hidden spots, outdoor routes, local gatherings — stays fragmented and hard to discover. There is no low-friction, safe way to meet people through real activities rather than random profiles.',
        ],
        audienceTitle: 'Who we serve',
        audience: [
          'Urban explorers and curious locals who want to experience their city differently',
          'Dating & socializing enthusiasts who want to meet real people through offline activities',
          'Local event organizers and small merchants who host activities and reach nearby audiences',
        ],
        marketTitle: 'Our market',
        market: 'We are launching first in Australia (Canberra, Sydney, and Melbourne), with regional expansion to New Zealand, Singapore, and other cities planned for late 2026.',
      },
      product: {
        title: 'Our product',
        body: 'omw is a mobile app (iOS & Android) that combines several real-world experiences into one platform:',
        features: [
          'Events & gatherings — discover and organize activities on a live map with AI-generated gear lists and safety tips',
          'Event & activity planner — an event- and location-driven AI agent that builds optimized day plans',
          'City Walk — location-based story scripts you play across real places',
          'Treasure hunt — hide and find capsules and blind-box surprises around the city',
          'Premium safety — one-tap SOS, community SOS broadcast, and encrypted sealed letters',
        ],
        stageTitle: 'Current stage of development',
        stages: [
          { label: 'Now · Q2 2026', text: 'Feature-complete iOS build in active QA; privacy and App Store compliance in progress.' },
          { label: 'Jul–Sep 2026', text: 'TestFlight public beta — early testers receive one year of omw Premium.' },
          { label: 'Mid-Sep 2026', text: 'iOS App Store launch, starting in Australia.' },
          { label: 'Q4 2026', text: 'Regional expansion to New Zealand, Singapore, and selected cities.' },
        ],
      },
      team: {
        title: 'Our team',
        intro: '',
        linkedinLabel: 'LinkedIn',
        members: [
          {
            name: 'Paul',
            role: 'Founder',
            bio: '7 years as Web Manager at the Australian National University.',
            linkedin: 'https://www.linkedin.com/in/yiyu-liu-a46985102/',
          },
          {
            name: 'Jun',
            role: 'COO',
            bio: 'B-side partnership BDM with 12+ years in Canberra local business across real estate, farming, and event organisation.',
            linkedin: 'https://www.linkedin.com/in/jun-xu-89216949/',
          },
          {
            name: 'Daniel',
            role: 'Developer',
            bio: 'Ph.D in LLM & Multimodal AI at the Australian National University.',
            linkedin: 'https://www.linkedin.com/in/gepeng-ji/',
          },
          {
            name: 'Kevin',
            role: 'Developer',
            bio: '6 years as a senior full-stack developer.',
            linkedin: '',
          },
        ],
        note: 'We are actively growing the team.',
      },
      contact: {
        title: 'Get in touch',
        body: 'Questions, partnerships, or press — reach us at ',
      },
    },
    footer: {
      about: 'About',
      support: 'Support',
      privacy: 'Privacy',
      terms: 'Terms',
      copy: '© {year} Dehors Tech Pty Ltd.',
    },
    media: {
      placeholder: 'App screenshot coming soon',
    },
    a11y: {
      openMenu: 'Open menu',
      closeMenu: 'Close menu',
      exploreMap: 'Explore the map',
    },
  },
  zh: {
    meta: {
      home: '在路上了 — 真实世界探索与玩法',
      about: '关于我们 — 在路上了',
      support: '支持 — 在路上了',
      privacy: '隐私政策 — 在路上了',
      terms: '服务条款 — 在路上了',
    },
    lang: { label: '语言', en: 'EN', zh: '中文' },
    nav: {
      features: '功能',
      premium: 'Premium',
      roadmap: '路线图',
      about: '关于',
      support: '支持',
      testflight: 'TestFlight',
      joinTestflight: '加入 TestFlight',
    },
    hero: {
      brand: '在路上了',
      typed: '只有线下的社群，才是真正的社群。',
      ctaPrimary: '加入 TestFlight 候补',
      ctaSecondary: '查看功能',
      mapTitle: '在路上了 城市地图',
    },
    coupon: {
      normal: '普通',
      medium: '中等',
      epic: '史诗',
    },
    features: {
      events: {
        eyebrow: '真实社交',
        title: '活动与聚会',
        body: '组织一场踢球、语言交换或快闪寻宝赛。发现附近正在发生的事，通过共同活动认识人——而不是 endless 滑动。',
        bullets: [
          '基于地图的活动发现',
          '在共同活动里交朋友——有语境的结识，而不是盲目滑动',
          '当天与进行中活动的 Story 轨道——实时掌握附近正在发生、随时可加入的非计划活动',
          'AI 沿途探店推荐：根据活动路线智能匹配餐厅、咖啡与补给点',
        ],
        mediaLabel: '活动 · 聚会与发现',
      },
      'ai-planner': {
        eyebrow: '活动与地点驱动',
        title: 'AI 行程规划',
        body: '基于你在 App 内感兴趣的活动、位置分享与都市探索，事件驱动型行程规划智能体会自动生成最优出行方案，让你最舒服地完成所有想做的事，并顺路探索所属城市与地区。',
        bullets: [
          '汇总已收藏的活动、地点分享与城市探索路线',
          '自动优化路线、时间安排与段间交通衔接',
          '围绕你的兴趣定制，而非千篇一律的模板行程',
        ],
        mediaLabel: 'AI 规划 · 事件驱动行程',
      },
      checkins: {
        eyebrow: '重要地点',
        title: '位置打卡',
        body: '向社区分享独特地点，记录你的真实旅程。AI 会自动估算热量消耗、生成所需装备清单，并结合地形与天气给出安全提示；打卡还会获得专属贴纸。',
        bullets: [
          'AI 根据距离、海拔与活动类型估算热量消耗',
          '为每个地点自动生成所需物品与装备清单',
          '结合地形、海拔与实时天气生成安全提示',
        ],
        mediaLabel: '打卡 · 贴纸与活动流程',
      },
      'offline-maps': {
        eyebrow: '走得更远',
        title: '户外探索',
        body: '不只是离线地图。记录并分享那些美丽却尚未被发现的路线。',
        bullets: [
          '徒步区域的离线地图包',
          '带海拔的 GPS 轨迹记录',
          '在社区分享路线',
        ],
        mediaLabel: '离线地图 · 轨迹记录',
      },
      'city-walk': {
        eyebrow: '城市故事',
        title: '全新版 City Walk',
        body: '看过《达·芬奇密码》吗？让 City Walk 变成那样的体验。',
        bullets: [
          '与真实地点绑定的分幕剧本',
          '根据你的路线、创意、主题、地点等，AI 为你生成所需剧本细节',
          '与社区其他人协作；约上好友一起探索。',
          'AI 沿途探店推荐：把咖啡、美食与休息站点智能融入都市探索路线',
        ],
        mediaLabel: 'City walk 剧本 · 分幕地图',
      },
      'treasure-hunt': {
        eyebrow: '发现与隐藏',
        title: '寻宝',
        bullets: [
          '把你的城市变成游乐场。在公园、小巷或咖啡馆外藏一个胶囊——附上谜题、照片或小礼物。其他人步行寻宝，找到惊喜！',
          '寻找收集商家随机藏下的盲盒宝藏。',
        ],
        mediaLabel: '寻宝 · 地图与胶囊流程',
      },
      premium: {
        title: '安全与密封信件',
        body: '为重要时刻而设。Premium 解锁一键 SOS 与实时 GPS、向附近社区成员推送的 SOS 广播、零知识加密密封信件，以及向所选紧急联系人即时告警。',
        bullets: [
          '一键 SOS — 紧急联系人即时收到你的实时位置',
          'SOS 广播：求救信号实时显示在社区地图上，并提醒附近可协助的社区成员',
          '无信号时通过加密蓝牙 Mesh 中继作为离线备份',
          '端到端加密密封信件，含 24 小时安全缓冲期',
        ],
        mediaLabel: 'Premium · SOS 与密封信件',
        linkLabel: '加入 TestFlight 候补',
      },
    },
    roadmap: {
      title: '路线图',
      lead: '2026 年 5 月 —— TestFlight 7–9 月，App Store 9 月中旬，年底前区域扩展。',
      phases: {
        'q2-2026': {
          period: '2026 Q2 · 当前',
          phase: '构建与打磨',
          status: '进行中',
          tasks: ['核心应用：寻宝、剧本、活动、离线地图', 'App Store 隐私与合规审查', '营销站与 TestFlight 准备'],
          details: [
            '功能完整的 iOS 版本正在积极 QA。',
            '隐私政策与 App Privacy Details 已与 App Store Connect 对齐。',
          ],
        },
        testflight: {
          period: '2026 年 7–9 月',
          phase: 'TestFlight 公测',
          status: '即将开始',
          tasks: ['公开 TestFlight 邀请波次', 'TestFlight 期间注册用户赠送 1 年 Premium 会员'],
          details: [
            'TestFlight 公测期间注册的用户，自动获得 1 年在路上了 Premium 会员。',
          ],
        },
        'app-store': {
          period: '2026 年 9 月中旬',
          phase: 'App Store 上线',
          status: '计划中',
          tasks: ['iOS App Store 发布', '澳大利亚首发市场', '上线城市 walk 剧本包'],
          details: [
            '目标：2026 年 9 月中旬在 App Store 发布。',
            '首发市场：澳大利亚（堪培拉、悉尼、墨尔本）。',
          ],
        },
        expansion: {
          period: '2026 Q4',
          phase: '区域扩展',
          status: '计划中',
          tasks: ['扩展至新西兰、新加坡及精选城市', '本地化剧本市场', '社区大使计划'],
          details: [
            '年底前从澳大利亚向其他区域 rollout。',
            '与当地创作者合作城市专属剧本内容。',
          ],
        },
      },
    },
    download: {
      subtitle: 'TestFlight 公测 · 2026 年 7–9 月 · 测试者终身会员',
      iosLabel: '加入 iOS TestFlight 公测',
      androidLabel: 'Android 即将推出',
    },
    progress: {
      label: '上线进度',
      pct: '距 TestFlight {pct}%',
    },
    homeIntro: {
      eyebrow: '什么是在路上了',
      title: '让你的城市，成为探索与结识他人的地方',
      body: '在路上了（omw，“on my way”）是一款真实世界探索与社交 App。发现附近正在发生的活动、跟随与真实地点绑定的剧本漫步、探索尚未被发现的户外路线、寻找社区宝藏——通过共同活动结识他人，而不是无休止地刷手机。内置 Premium 安全功能，为每一次出行保驾护航。',
      bullets: [
        '基于地图的附近活动与聚会',
        'AI 行程规划、离线地图与 GPS 轨迹记录',
        'City Walk 剧本漫步与社区寻宝',
        'Premium 安全：一键 SOS 与加密密封信件',
      ],
      cta: '了解更多关于我们',
    },
    about: {
      hero: {
        eyebrow: '关于在路上了',
        title: '我们帮助人们重新发现真实世界——一起',
        subtitle: '在路上了（omw，“on my way”）把你的城市，变成一个可以探索、玩乐、并在线下结识他人的地方。',
      },
      business: {
        title: '我们做什么',
        body: [
          '在路上了是一个真实世界探索与社交平台。我们将基于地图的活动发现、AI 行程规划、位置打卡、离线地图、与真实地点绑定的 City Walk 剧本，以及社区寻宝整合在一起——让人们发现身边真正在发生的事，并通过共同活动建立连接。',
          '我们要解决的问题：如今的社交 App 让人们独自不停地刷屏，而真实世界中真正值得去做的事——活动、隐秘地点、户外路线、本地聚会——却分散且难以发现。人们缺少一种低门槛、安全的方式，通过真实活动而非陌生资料去结识他人。',
        ],
        audienceTitle: '我们服务谁',
        audience: [
          '想以不同方式体验城市的都市探索者与好奇的本地人',
          '寻找并分享新路线与地点的户外与徒步爱好者',
          '希望通过共同活动结识他人的学生与年轻职场人（18–35 岁）',
          '举办活动、触达附近人群的本地活动组织者与小商家',
        ],
        marketTitle: '我们的市场',
        market: '我们首先在澳大利亚（堪培拉、悉尼、墨尔本）上线，并计划于 2026 年底前扩展至新西兰、新加坡及其他城市。',
      },
      product: {
        title: '我们的产品',
        body: '在路上了是一款移动应用（iOS 优先），将多种真实世界体验整合到一个平台中：',
        features: [
          '活动与聚会——在实时地图上发现并组织活动',
          'AI 行程规划——事件与位置驱动的智能体，生成最优的一日行程',
          '位置打卡——分享独特地点，附带 AI 生成的装备清单与安全提示',
          '户外探索——用于徒步路线的离线地图与 GPS 轨迹记录',
          'City Walk——与真实地点绑定、可在城市中游玩的剧本',
          '寻宝——在城市各处藏匿与寻找胶囊及盲盒惊喜',
          'Premium 安全——一键 SOS、社区 SOS 广播与加密密封信件',
        ],
        stageTitle: '当前开发阶段',
        stages: [
          { label: '当前 · 2026 Q2', text: '功能完整的 iOS 版本正在积极 QA；隐私与 App Store 合规审查进行中。' },
          { label: '2026 年 7–9 月', text: 'TestFlight 公测——早期测试者获赠 1 年在路上了 Premium。' },
          { label: '2026 年 9 月中旬', text: 'iOS App Store 上线，首发澳大利亚。' },
          { label: '2026 Q4', text: '区域扩展至新西兰、新加坡及精选城市。' },
        ],
      },
      team: {
        title: '我们的团队',
        intro: '在路上了由一支精简、亲力亲为的创始团队打造，融合了产品、商务拓展、AI 研究与全栈工程经验。',
        linkedinLabel: 'LinkedIn',
        members: [
          {
            name: 'Paul',
            role: '创始人',
            bio: '曾在澳大利亚国立大学担任 Web 经理 7 年。',
            linkedin: 'https://www.linkedin.com/in/yiyu-liu-a46985102/',
          },
          {
            name: 'Jun',
            role: '首席运营官（COO）',
            bio: 'B 端合作与商务拓展（BDM），在堪培拉本地房地产、农业与活动组织领域深耕 12 年以上。',
            linkedin: 'https://www.linkedin.com/in/jun-xu-89216949/',
          },
          {
            name: 'Daniel',
            role: '开发工程师',
            bio: '澳大利亚国立大学 LLM 与多模态方向博士。',
            linkedin: 'https://www.linkedin.com/in/gepeng-ji/',
          },
          {
            name: 'Kevin',
            role: '开发工程师',
            bio: '6 年资深全栈开发经验。',
            linkedin: '',
          },
        ],
        note: '我们正在积极扩充团队。',
      },
      contact: {
        title: '联系我们',
        body: '有任何问题、合作或媒体咨询，请联系 ',
      },
    },
    footer: {
      about: '关于',
      support: '支持',
      privacy: '隐私',
      terms: '条款',
      copy: '© {year} Dehors Tech Pty Ltd.',
    },
    media: {
      placeholder: '应用截图即将上线',
    },
    a11y: {
      openMenu: '打开菜单',
      closeMenu: '关闭菜单',
      exploreMap: '探索地图',
    },
  },
};
